import { Subject } from 'rxjs'
import { debounceTime, first, map, mergeMap, takeUntil, tap } from 'rxjs/operators'
import { ClientConfig } from '../config/ClientConfig'
import { PlayerAvatar, PlayerItemView } from '../communication/PlayerApiProtocol.generated'
import { TimedCache } from '../script-utils'
import { HttpService } from './http-service'

export const PlayerProfileService = new class {
	// Cache avatar requests for 20 seconds to prevent poor React state management from spamming requests.
	private avatarRequestCache = new TimedCache<Promise<PlayerAvatar>>(20_000)

	private avatarRequestId$ = new Subject<number>()
	private avatarSuccess$ = new Subject<PlayerAvatar>()
	private avatarFailed$ = new Subject<number>()

	constructor() {
		let idBuffer = []

		// The below chain is seemingly complicated because it allows for lossless debounced inputs
		// as well as multiple requests-in-flight.
		this.avatarRequestId$
			.pipe(
				tap(n => idBuffer.push(n)),
				// Wait at least 300ms since the last request before sending the batch.
				debounceTime(300),
				// Dedupe and pass along buffered Ids
				map(() => Array.from(new Set(idBuffer))),
				// Clear buffer
				tap(() => (idBuffer = [])),
				// Send batch request
				mergeMap(ids => this.batchRequestAvatars(ids)),
				// Unroll batches into individual avatars
				mergeMap(av => av),
			)
			.subscribe(this.avatarSuccess$)
	}

	/**
     * Gets a player's avatar either from the local cache or as part of the next batch request.
     * Batching is debounced so continued requests will delay the request+response.
     */
	getPlayerAvatar(playerId: number): Promise<PlayerAvatar> {
		const cached = this.avatarRequestCache.get('' + playerId)
		if (cached) return cached

		const response = new Promise<PlayerAvatar>((resolve, reject) => {
			const success = this.avatarSuccess$.pipe(first(a => a.userId === playerId))
			const failed = this.avatarFailed$.pipe(first(id => id === playerId))

			success.pipe(takeUntil(failed)).subscribe(resolve)
			failed.pipe(takeUntil(success)).subscribe(reject)
		})

		this.avatarRequestId$.next(playerId)

		this.avatarRequestCache.put('' + playerId, response)
		return response
	}

	getItemsByNameIds(nameIds: string[]): Promise<PlayerItemView[]> {
		const queryParams = nameIds.map(id => 'nameId=' + id).join('&')

		const url = `${ClientConfig.PROFILE_SERVICE_BASE_URL}/item?${queryParams}`

		return HttpService.get<PlayerItemView[]>(url)
	}

	private batchRequestAvatars(ids: number[]): Promise<PlayerAvatar[]> {
		const queryParams = ids.map(id => 'playerId=' + id).join('&')

		const url = `${ClientConfig.PROFILE_SERVICE_BASE_URL}/player/avatar/batch?${queryParams}`

		return (
			HttpService.get<PlayerAvatar[]>(url)
			// If the batch fails, notify of each failure and return no successful avatars.
				.catch(() => {
					ids.forEach(id => this.avatarFailed$.next(id))
					return []
				})
		)
	}
}()
